import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toDateTimeString } from "../../utilities/dateUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import RedactionPane from '../breaches/_redactionPane';
import api from '../api/api';
import { saveAs } from 'file-saver';
import Loader from './loader';
import { ErrorModal } from '../../utilities/errorModal';

export const BreachAttachmentsDisplay = ({
    attachments,
    imageAttachments,
    onChange,
    showIsLetterImage,
    onIsLetterImageChange,
    onDeleteClick,
    organisationId,
    token,
    userId,
    isOldLayout
}) => {
    const attachmentsLayoutKey = `${userId}-breachAttachmentLayoutPreference`;
    const [viewLayout, setViewLayout] = useState(localStorage.getItem(attachmentsLayoutKey) ?? 'grid');
    const [emailModal, setEmailModal] = useState(null);
    const [downloadError, setDownloadError] = useState(null)
    const [downloadCertificateId, setDownloadCertificateId] = useState(null)

    const [defaultTab] = useState(() => {
        const hasImages = attachments.some(attachment => attachment.isImage);
        const hasDocuments = attachments.some(attachment => !attachment.isImage);
        return (!hasImages && hasDocuments) ? 'files' : 'images';
    });

    const images = attachments.filter(attachment => attachment.isImage);
    const files = attachments.filter(attachment => !attachment.isImage);

    const renderFileIcon = (attachment) => {
        if (attachment.isEmail) {
            return 'fas fa-paper-plane';
        }
        switch (attachment.contentType) {
            case "application/pdf": return "far fa-file-pdf";
            case "application/word.doc": return "far fa-file-word";
            default: return "far fa-file-alt";
        }
    };

    const downloadPostageCertificate = (attachment) => {
        setDownloadError(null)
        setDownloadCertificateId(attachment.id)
        api.downloadPostageCertificate(
            (data, filename) => {
                if (data.isError) {
                    setDownloadError(data.errorMessage)
                }
                else {
                    saveAs(data, filename)
                    setDownloadError(null)
                }
                setDownloadCertificateId(null)
            },
            error => {
                setDownloadCertificateId(null)
                setDownloadError(error.errorMessage)
                console.log(error)
            },
            attachment.letterId, organisationId, token
        )
    }

    const postageCertificateDownloadButton = (attachment) => <button
        disabled={ downloadCertificateId === attachment.id }
        className={`btn btn-none m-0 p-0 ml-2 ${viewLayout === "list" ? "mr-2" : "" }`} type='button'
        onClick={ () => downloadPostageCertificate(attachment) }
    >
        {
            downloadCertificateId === attachment.id
                ? <div className='tiny-loader'>
                    <Loader />
                </div>
                : <i className="fas fa-award"></i>
        }
    </button>

    const renderImageGridCard = (attachment) => (
        <div key={ attachment.id } className="card rounded attachment-card-grid">
            <div className="attachment-card-grid-media breach-attachment-media">
                <RedactionPane
                    organisationId={ organisationId }
                    token={ token }
                    onChange={ onChange }
                    id={ attachment.id }
                    url={ attachment.url }
                    isAttachment={ true }
                    images={ imageAttachments }
                    initialIndex={ imageAttachments.findIndex(imageAttachment => imageAttachment.id === attachment.id) }
                />
            </div>
            <div className="card-body px-3 py-2">
                <div className="d-flex justify-content-between">
                    <h6 className="mb-1 attachment-card-grid-filename text-truncate" title={ attachment.filename }>{ attachment.filename }</h6>
                    <button onClick={ () => onDeleteClick(attachment.id) } className="btn btn-none p-0 m-0 ml-1 col-1"><i className="fas fa-trash-alt"></i></button>
                </div>
                <div className="d-flex align-items-center">
                    <label className="m-0">Date:&nbsp;</label>
                    <small className="m-0">{ toDateTimeString(attachment.timestamp, true) }</small>
                </div>
                { showIsLetterImage && (
                    <div className="d-flex align-items-center mt-1">
                        <label className="m-0 mr-1">Is Letter Image</label>
                        <input type="checkbox" checked={ attachment.isLetterImage } onChange={ e => onIsLetterImageChange(attachment.id, e.target.checked) } />
                    </div>
                ) }
            </div>
        </div>
    );

    const renderImageListCard = (attachment) => (
        <div key={ attachment.id } className="card rounded mb-2 d-flex flex-row align-items-center w-100">
            <div className="d-flex align-items-center pl-2 py-1 attachment-card-list-thumbnail breach-attachment-media">
                <RedactionPane
                    organisationId={ organisationId }
                    token={ token }
                    onChange={ onChange }
                    id={ attachment.id }
                    url={ attachment.url }
                    isAttachment={ true }
                    images={ imageAttachments }
                    initialIndex={ imageAttachments.findIndex(imageAttachment => imageAttachment.id === attachment.id) }
                />
            </div>
            <div className="card-body pl-3 pr-1 py-2 d-flex justify-content-between align-items-center">
                <div className="row w-100 align-items-center">
                    <div className="col-4" style={{ minWidth: 0 }}>
                        <h6 className="mb-0 text-truncate" title={ attachment.filename }>{ attachment.filename }</h6>
                    </div>
                    <div className="col-3 d-flex align-items-center">
                        <small className="text-muted m-0 mr-1">Date:</small>
                        <small className="m-0">{ toDateTimeString(attachment.timestamp, true) }</small>
                    </div>
                    { showIsLetterImage && (
                        <div className="col-3 d-flex align-items-center">
                            <small className="text-muted m-0 mr-1">Is Letter Image</small>
                            <input type="checkbox" checked={ attachment.isLetterImage } onChange={ e => onIsLetterImageChange(attachment.id, e.target.checked) } />
                        </div>
                    ) }
                    <div className='col-2 d-flex justify-content-end'>
                        <button onClick={ () => onDeleteClick(attachment.id) } className="btn btn-none p-0 m-0 ml-1"><i className="fas fa-trash-alt"></i></button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFileGridCard = (attachment) => (
        <div key={ attachment.id } className="card rounded attachment-card-grid file-card">
            <div className="attachment-card-grid-media d-flex justify-content-center align-items-center"
                 onClick={ attachment.isEmail ? () => setEmailModal(attachment) : undefined }
                 style={ attachment.isEmail ? { cursor: 'pointer' } : {} }>
                <i className={ `fa-6x ${ renderFileIcon(attachment) }` }></i>
            </div>
            <div className="card-body px-3 py-2">
                <div className="d-flex justify-content-between align-items-start">
                    { attachment.isEmail
                        ? <h6 className="mb-0 attachment-card-grid-filename text-truncate" title={ attachment.subject }>{ attachment.subject }</h6>
                        : <a href={ attachment.url } target="_blank" className="attachment-card-grid-filename"><h6 className="mb-0 text-truncate" title={ attachment.filename }>{ attachment.filename }</h6></a>
                    }
                    { attachment.isEmail
                        ? <button className="btn btn-none p-0 m-0 mr-2 flex-shrink-0" onClick={ () => setEmailModal(attachment) }><i className="fas fa-eye"></i></button>
                        : <>
                            { attachment.isLetter && postageCertificateDownloadButton(attachment) }
                            <a href={ attachment.url } target="_blank" className="btn btn-none p-0 m-0 ml-2 flex-shrink-0"><i className="fas fa-download"></i></a>
                        </>
                    }
                </div>
                <div className="d-flex align-items-center mt-2">
                    <label className="m-0">Date:&nbsp;</label>
                    <small className="m-0">{ toDateTimeString(attachment.timestamp) }</small>
                </div>
            </div>
        </div>
    );

    const renderFileListCard = (attachment) => (
        <div key={ attachment.id } className="card rounded mb-2 d-flex flex-row align-items-center w-100 file-card">
            <div className="d-flex justify-content-center align-items-center pl-2 py-1 attachment-card-list-thumbnail"
                 onClick={ attachment.isEmail ? () => setEmailModal(attachment) : undefined }
                 style={ attachment.isEmail ? { cursor: 'pointer' } : {} }>
                <i className={ `fa-2x ${ renderFileIcon(attachment) }` }></i>
            </div>
            <div className="card-body px-3 py-2 d-flex justify-content-between align-items-center">
                <div className="row w-100 align-items-center">
                    <div className="col-4" style={{ minWidth: 0 }}>
                        { attachment.isEmail
                            ? <h6 className="mb-0 text-truncate" title={ attachment.subject }>{ attachment.subject }</h6>
                            : <a href={ attachment.url } target="_blank" className="d-block text-truncate"><h6 className="mb-0 text-truncate" title={ attachment.filename }>{ attachment.filename }</h6></a>
                        }
                    </div>
                    <div className="col-3 d-flex align-items-center">
                        <small className="text-muted m-0 mr-1">Date:</small>
                        <small className="m-0">{ toDateTimeString(attachment.timestamp) }</small>
                    </div>
                </div>
                <div className="flex-shrink-0">
                    { attachment.isEmail
                        ? <button className="btn btn-none p-0 m-0" onClick={ () => setEmailModal(attachment) }><i className="fas fa-eye"></i></button>
                        : <>
                            { attachment.isLetter && postageCertificateDownloadButton(attachment) }
                            <a href={ attachment.url } target="_blank" className="btn btn-none p-0 m-0"><i className="fas fa-download"></i></a>
                        </>
                    }
                </div>
            </div>
        </div>
    );

    const renderEmpty = (label) => (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
            <i className="far fa-folder-open fa-2x mb-3"></i>
            <p className="m-0">{ label }</p>
        </div>
    );

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <ul className="nav nav-tabs w-100" id="breachAttachmentsTabbed" role="tablist">
                    <li className="nav-item" role="presentation">
                        <button className={ `nav-link ${ defaultTab === 'images' ? 'active' : '' }` } id="breach-images-tab" data-toggle="tab" data-target="#breach-images" type="button" role="tab" aria-controls="breach-images" aria-selected={ defaultTab === 'images' }>
                            <i className='far fa-file-image mr-2'></i>
                            Images
                            <span className={ `ml-2 badge ${isOldLayout && "rounded-pill"} bg-${ images.length === 0 ? "secondary" : "success" }` }>{ images.length }</span>
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button className={ `nav-link ${ defaultTab === 'files' ? 'active' : '' }` } id="breach-files-tab" data-toggle="tab" data-target="#breach-files" type="button" role="tab" aria-controls="breach-files" aria-selected={ defaultTab === 'files' }>
                            <i className='far fa-list-alt mr-2'></i>
                            Documents
                            <span className={ `ml-2 badge ${isOldLayout && "rounded-pill"} bg-${ files.length === 0 ? "secondary" : "success" }` }>{ files.length }</span>
                        </button>
                    </li>
                </ul>
                <div className="btn-group">
                    <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" data-display="static">
                        { viewLayout === 'grid'
                            ? <><i className='fas fa-border-all mr-2'></i>Grid</>
                            : <><i className='far fa-list-alt mr-2'></i>List</>
                        }
                    </button>
                    <div className="dropdown-menu dropdown-menu-right p-0">
                        <button onClick={ () => { setViewLayout('grid'); localStorage.setItem(attachmentsLayoutKey, 'grid'); } } className="dropdown-item p-2 w-fit">
                            <i className='fas fa-border-all mr-2'></i>Grid
                        </button>
                        <button onClick={ () => { setViewLayout('list'); localStorage.setItem(attachmentsLayoutKey, 'list'); } } className="dropdown-item p-2 w-fit">
                            <i className='far fa-list-alt mr-2'></i>List
                        </button>
                    </div>
                </div>
            </div>
            <div className="tab-content" id="breachAttachmentsTabbedContent">
                <div className={ `tab-pane fade p-2 bg-white ${ defaultTab === 'images' ? 'show active' : '' }` } id="breach-images" role="tabpanel" aria-labelledby="breach-images-tab">
                    { images.length === 0
                        ? renderEmpty('No images found')
                        : <div className={ viewLayout === 'list' ? '' : 'attachment-grid-container' }>
                            { images.map(attachment => viewLayout === 'list' ? renderImageListCard(attachment) : renderImageGridCard(attachment)) }
                          </div>
                    }
                </div>
                <div className={ `tab-pane fade p-2 bg-white ${ defaultTab === 'files' ? 'show active' : '' }` } id="breach-files" role="tabpanel" aria-labelledby="breach-files-tab">
                    { files.length === 0
                        ? renderEmpty('No documents found')
                        : <div className={ viewLayout === 'list' ? '' : 'attachment-grid-container' }>
                            { files.map(attachment => viewLayout === 'list' ? renderFileListCard(attachment) : renderFileGridCard(attachment)) }
                          </div>
                    }
                </div>
            </div>

            { emailModal !== null && (
                <Modal show onHide={ () => setEmailModal(null) } size="lg">
                    <Modal.Header>
                        <Modal.Title>
                            { !stringHelpers.isNullOrEmpty(emailModal.subject) ? emailModal.subject : <>Email</> }
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="px-1">
                            <p><b>Date: </b>{ toDateTimeString(emailModal.timestamp) }</p>
                            <p><b>To: </b>{ emailModal.recipients }</p>
                            <p className='text-pre-wrap'>{ emailModal.content }</p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-sm btn-secondary mt-1" onClick={ () => setEmailModal(null) }>Close</button>
                    </Modal.Footer>
                </Modal>
            ) }

            {
                !stringHelpers.isNullOrEmpty(downloadError)
                && <ErrorModal 
                    message={downloadError}
                    onHide={() => setDownloadError(null)}
                />
            }
        </>
    );
};
